import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { NeuButton, NeuCard, NeuInput } from "../../components/neu/Neu";
import { decideRequest } from "../../api/hr";
import { LeaveConflictWarning } from "./LeaveConflictWarning";
import { fileUrlAuth } from "../../api/fileUrl";
import { RefBadge } from "../../components/neu/RefBadge";
import { useApiError } from "../../hooks/useApiError";

const DOC_KIND = { advances: "advance", resignations: "resignation", leaves: "leave" };

export const ApprovalCard = ({ kind, item, reload }) => {
  const { t } = useTranslation();
  const describe = useApiError();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (action) => {
    setBusy(true);
    try {
      await decideRequest(kind, item.id, action, note || null);
      toast.success(t(`approvals.${action}d`));
      window.dispatchEvent(new Event("helcoo:approvals-refresh"));
      reload();
    } catch (error) {
      toast.error(describe(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <NeuCard className="animate-rise-in p-5" data-testid={`approval-${kind}-${item.id}`}>
      <p className="text-sm font-black">{item.user_name || item.user_id}</p>
      <RefBadge value={item.ref_no} className="mt-1" />
      <p className="mt-1 text-xs tracking-normal text-muted-foreground">{t(`myRequests.${DOC_KIND[kind]}`)}</p>
      <dl className="mt-4 grid grid-cols-2 gap-3 font-mono text-xs">
        {kind === "advances" ? (
          <>
            <Item label={t("advances.amount", { currency: item.currency })} value={item.amount} />
            <Item label={t("advances.months")} value={item.repayment_months} />
            <Item label={t("approvals.installment")} value={item.monthly_installment} />
            <Item label={t("approvals.maxAllowed")} value={item.max_allowed} />
          </>
        ) : kind === "leaves" ? (
          <>
            <Item label={t("leaves.from")} value={item.start_date} />
            <Item label={t("leaves.to")} value={item.end_date} />
            <Item label={t("leaves.days")} value={item.total_days} />
            <Item label={t("leaves.paidUnpaid")} value={`${item.paid_days} / ${item.unpaid_days}`} />
          </>
        ) : (
          <>
            <Item label={t("resignation.noticeDaysShort")} value={item.notice_days} />
            <Item label={t("resignation.lastDay")} value={item.last_working_day} />
            <Item label={t("approvals.submittedOn")} value={item.submitted_on} />
          </>
        )}
      </dl>
      {item.reason ? <p className="mt-3 text-xs text-muted-foreground">“{item.reason}”</p> : null}
      {item.certificate_url ? (
        <a href={fileUrlAuth(item.certificate_url)} target="_blank" rel="noreferrer"
          data-testid={`approval-certificate-${item.id}`}
          className="mt-3 block text-xs font-bold text-primary underline">{t("leaves.certificateView")}</a>
      ) : null}
      {kind === "leaves" ? <LeaveConflictWarning requestId={item.id} /> : null}
      <NeuInput className="mt-4" data-testid={`approval-note-${item.id}`} placeholder={t("approvals.note")}
        value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="mt-3 flex gap-2">
        <NeuButton variant="primary" className="flex items-center gap-2" data-testid={`approval-approve-${item.id}`} disabled={busy} onClick={() => decide("approve")}>
          <Check className="h-4 w-4" /> {t("approvals.approve")}
        </NeuButton>
        <NeuButton variant="danger" className="flex items-center gap-2" data-testid={`approval-reject-${item.id}`} disabled={busy} onClick={() => decide("reject")}>
          <X className="h-4 w-4" /> {t("approvals.reject")}
        </NeuButton>
      </div>
    </NeuCard>
  );
};

const Item = ({ label, value }) => (
  <div>
    <dt className="text-[10px] tracking-normal text-muted-foreground">{label}</dt>
    <dd className="mt-0.5 font-semibold">{value}</dd>
  </div>
);
